/**
 * UnsavedChangesDialog Component
 *
 * Dialog de confirmación personalizado para cambios no guardados.
 * Mejor UX que window.confirm() nativo con estilo consistente de la aplicación.
 *
 * @example
 * ```tsx
 * <UnsavedChangesDialog
 *   open={showDialog}
 *   onOpenChange={setShowDialog}
 *   onConfirm={handleDiscardChanges}
 * />
 * ```
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface UnsavedChangesDialogProps {
  /** Si el dialog está abierto */
  open: boolean;
  /** Callback cuando cambia el estado open */
  onOpenChange: (open: boolean) => void;
  /** Callback cuando el usuario confirma descartar cambios */
  onConfirm: () => void;
  /** Callback cuando el usuario cancela (opcional, por defecto cierra el dialog) */
  onCancel?: () => void;
  /** Mensaje personalizado (opcional) */
  message?: string;
  /** Título personalizado (opcional) */
  title?: string;
  /** Texto del botón de confirmar (opcional) */
  confirmText?: string;
  /** Texto del botón de cancelar (opcional) */
  cancelText?: string;
}

export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  message = 'Tienes cambios sin guardar. Si cierras ahora, estos cambios se perderán.',
  title = '¿Descartar cambios?',
  confirmText = 'Descartar cambios',
  cancelText = 'Continuar editando'
}: UnsavedChangesDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
